import {
  FC,
  ReactNode,
  useState,
  MutableRefObject,
  useEffect,
  memo,
  Key,
  useRef,
} from 'react';
// 组件
import { ProTable, ProTooltip } from '../../antd';
// 方法
import classnames from 'classnames';
import { moneyFormat } from '../../utils/dataUtils';
// 常量
import { columnsProps } from '../interface';
import { SUCCESS_STATUS_CODE, } from '../../constants/constant';
import { IObject } from '../../constants/interface';

export interface ITotalItem {
  format?: (value: any) => ReactNode;
  dataIndex: string;
  key: string;
}
export interface BaseTableTotalProps {
  /** 需要总计的字段 */
  totalKeys?: ITotalItem[] | false;
  /** 总计在哪一列显示 */
  totalIndex?: string;
  /** 总计文字 */
  totalText?: ReactNode;
  /** 总计接口 */
  totalApi?: (parasm?: any) => Promise<any>;
  /** 总计参数 */
  totalApiParams?: any;
  /** 总计值对应字段 */
  totalDataKey?: string;
  /** 总计数据 */
  totalData?: any;
}
export interface TableTotalAction {
  reload: (params: any) => void;
}
export type TableTotalActionType = TableTotalAction | undefined;
export interface TableTotalProps extends BaseTableTotalProps {
  totalRef: MutableRefObject<TableTotalActionType>;
  columns?: columnsProps[];
  hasSelect?: boolean;
}
/** 表格总计 */
const TableTotal: FC<TableTotalProps> = memo(props => {
  const {
    columns,
    totalKeys,
    totalIndex,
    totalText,
    totalApiParams,
    totalDataKey,
    hasSelect,
    totalRef,
    totalData: totalDataProps,
    totalApi,
  } = props;
  const columnPointer = useRef<number>(0);
  const [totalData, setTotalData] = useState<any>({});
  useEffect(() => {
    setTotalData(totalDataProps || {});
  }, [totalDataProps]);
  if (!columns || !columns.length) return null;
  if (!totalKeys) return null;
  if (!totalApi && !totalDataProps) return null;
  const getTotalData = (listParams?: any) => {
    if (totalApi) {
      totalApi({ ...totalApiParams, ...listParams }).then(res => {
        if (res.code === SUCCESS_STATUS_CODE) {
          const data = (totalDataKey ? res.data[totalDataKey] : res.data) ?? {};
          if (Array.isArray(data)) {
            setTotalData(data);
          } else {
            const result: any = {};
            totalKeys.forEach(item => {
              result[item.dataIndex] = item.format ? item.format(data[item.key]) : data[item.key];
            });
            setTotalData(result);
          }
        }
      });
    } else {
      setTotalData(totalDataProps);
    }
  };
  const defSubTotalIndex = totalIndex || columns[0].dataIndex;
  const newColumns = [...columns];
  if (hasSelect) newColumns.unshift({ width: 369 });
  const renderValue = (column: columnsProps, rowIndex?: number) => {
    const { dataIndex } = column;
    if (dataIndex === defSubTotalIndex && rowIndex === 0) return totalText;
    if (dataIndex && typeof dataIndex === 'string') {
      const columnText =
        Array.isArray(totalData) && typeof rowIndex === 'number'
          ? totalData[rowIndex]?.[dataIndex]
          : totalData[dataIndex];
      if (columnText !== undefined) {
        return formatValue(columnText, column);
      }
      return '';
    }
    return '';
  };
  const formatValue = (value: any, column: columnsProps) => {
    const { renderType, moneyProps = {} } = column;
    if (renderType === 'money') {
      const { color, fontWeight, fontSize } = moneyProps;
      const moneyStr: string = moneyFormat(value, moneyProps);
      return (
        <ProTooltip title={moneyStr}>
          <span style={{ color, fontWeight, fontSize }}>{moneyStr}</span>
        </ProTooltip>
      );
    }
    if (typeof value === 'string') {
      return <ProTooltip title={value}>{value}</ProTooltip>;
    }
    return value;
  };
  /**
   * 生成合计列
   * @param renderColumns 列配置项集合
   * @param rowIndex 第几行
   * @param isResetPointer 是否重置列指针
   * @returns 
   */
  const renderCell = (
    renderColumns: columnsProps[],
    rowIndex: number,
    isResetPointer?: boolean,
  ): ReactNode[] => {
    const newCell: ReactNode[] = [];
    if (isResetPointer) columnPointer.current = 0;
    renderColumns?.forEach((column, index) => {
      const { render, ellipsis, fixed, children, dataIndex, ...resColumnProps } = column;
      const classes = classnames('ant-table-summary-td', {
        'yb-line-ellipsis': !!ellipsis,
      });
      if (Array.isArray(children) && children.length) {
        const childrenCell = renderCell(children, rowIndex, false);
        return newCell.push(...childrenCell);
      }
      newCell.push(
        <ProTable.Summary.Cell key={columnPointer.current} index={columnPointer.current} {...resColumnProps}>
          <div className={classes}>{renderValue(column, rowIndex)}</div>
        </ProTable.Summary.Cell>
      );
      columnPointer.current = columnPointer.current + 1;
    });
    return newCell;
  };
  const renderHeadCell = (
    renderColumns: columnsProps[],
    parentKey?: Key,
  ): ReactNode[] => {
    const newHealCell: ReactNode[] = [];
    renderColumns?.forEach((column, index) => {
      const currentKey: Key = parentKey ? `${parentKey}_${index}` : index;
      const { width, children } = column;
      if (Array.isArray(children) && children.length) {
        const childrenHeadCell = renderHeadCell(children, currentKey);
        return newHealCell.push(...childrenHeadCell);
      }
      if (index === 0 && width === 369) {
        newHealCell.push(<col key={index} className="ant-table-selection-col" />);
      } else {
        newHealCell.push(<col key={currentKey} style={{ width: width, minWidth: width }} />);
      }
    });
    return newHealCell;
  };
  /** @多行总计 */
  const renderArrayData = () => {
    return totalData.map((item: IObject, index: number) => {
      return (
        <ProTable.Summary.Row
          key={`ant-table-row ant-table-row-level-${index}`}
          className={`ant-table-row ant-table-row-level-${index}`}
        >
          {renderCell(newColumns, index, true)}
        </ProTable.Summary.Row>
      );
    });
  };
  totalRef.current = {
    reload: getTotalData,
  };
  return (
    <table className="an-table yb-table-total" style={{ tableLayout: 'fixed' }}>
      <colgroup>
        {renderHeadCell(newColumns)}
      </colgroup>
      <tbody className="ant-table-tbody">
        {Array.isArray(totalData) ? (
          renderArrayData()
        ) : (
          <ProTable.Summary.Row className="ant-table-row ant-table-row-level-0">
            {renderCell(newColumns, 0, true)}
          </ProTable.Summary.Row>
        )}
      </tbody>
    </table>
  );
});

TableTotal.defaultProps = {
  totalKeys: false,
  totalText: '总计',
  hasSelect: false,
};

export default TableTotal;
